import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api, { fmt, fmtDate } from '../../utils/api';

export default function UploadCenterPage() {
  const { t } = useTranslation();
  const [uploadResult, setUploadResult] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const fetchFiles = useCallback(async () => {
    try {
      const [ordersRes] = await Promise.all([
        api.get('/orders'),
      ]);
      setOrders(ordersRes.data || []);
      // Get files for all orders
      const orderNos = (ordersRes.data || []).map(o => o.orderNo);
      const filePromises = orderNos.map(no => api.get(`/data-requests/files/${no}`).catch(() => ({ data: [] })));
      const fileResults = await Promise.all(filePromises);
      const files = [];
      fileResults.forEach((res, i) => {
        (res.data || []).forEach(f => files.push({ ...f, orderNo: orderNos[i] }));
      });
      setAllFiles(files);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    try {
      const res = await api.post('/data-requests/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      if (fileRef.current) fileRef.current.value = '';
      fetchFiles();
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
  };

  const downloadFile = async (file) => {
    try {
      const res = await api.get(`/data-requests/download/${file.id}`, { responseType: 'blob' });
      const isPdf = file.originalName?.toLowerCase().endsWith('.pdf');
      const blob = new Blob([res.data], { type: isPdf ? 'application/pdf' : 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      if (isPdf) window.open(url, '_blank');
      else { const a = document.createElement('a'); a.href = url; a.download = file.originalName; a.click(); }
    } catch { alert('Download failed'); }
  };

  const fileTypeLabel = (type) => {
    switch(type) {
      case 'invoice': return { label: 'Invoice', bg: '#dbeafe', color: '#1565c0' };
      case 'packing_list': return { label: 'Packing List', bg: '#fef3c7', color: '#92400e' };
      case 'bank_transfer': return { label: 'Bank Transfer', bg: '#dcfce7', color: '#16a34a' };
      default: return { label: 'Other', bg: '#f1f5f9', color: '#64748b' };
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>Upload Center</h2>

      {/* Upload area */}
      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
          Drop files here. Files with SI/SO numbers in the filename are auto-matched to orders.
          Supports: invoices, packing lists, bank transfer confirmations.
        </p>
        <div style={{ border: '2px dashed #cbd5e1', borderRadius: 12, padding: 32, textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#1565c0'; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; }}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#cbd5e1'; const dt = e.dataTransfer; if (dt.files.length) { const input = fileRef.current; input.files = dt.files; handleUpload({ target: input }); } }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>📁</div>
          <div style={{ fontSize: 16, color: '#374151', fontWeight: 600 }}>Click or drag files here</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>PDF, JPG, PNG, Excel (max 20MB per file)</div>
          <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx" style={{ display: 'none' }} onChange={handleUpload} />
        </div>

        {/* Upload results */}
        {uploadResult && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{uploadResult.uploaded} file(s) uploaded:</div>
            {uploadResult.files.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 18 }}>{f.matched ? '✅' : '❌'}</span>
                <span style={{ fontWeight: 500 }}>{f.originalName}</span>
                <span className="text-muted">→</span>
                {f.matched ? (
                  <span style={{ fontWeight: 600, color: '#16a34a' }}>{f.orderNo} ({f.fileType})</span>
                ) : (
                  <span style={{ color: '#dc2626' }}>No match — upload to specific order below</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Files by order */}
      <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>Files by Order</h3>
      {orders.filter(o => o.docType === 'SI').map(order => {
        const orderFiles = allFiles.filter(f => f.orderNo === order.orderNo);
        return (
          <div key={order.orderNo} className="card" style={{ padding: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: orderFiles.length ? 8 : 0 }}>
              <span className="fw-bold">{order.orderNo}</span>
              <span className={`badge badge-${(order.origin || '').toLowerCase()}`}>{order.origin}</span>
              <span className="text-muted" style={{ fontSize: 12 }}>{fmt(order.totals?.amount)}</span>
              {order.paymentStatus === 'paid' && <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#dcfce7', color: '#16a34a', border: '1px solid #16a34a' }}>PAID</span>}
              <div style={{ flex: 1 }} />
              <span className="text-muted" style={{ fontSize: 11 }}>{orderFiles.length} files</span>
            </div>
            {orderFiles.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {orderFiles.map(f => {
                  const ft = fileTypeLabel(f.fileType);
                  return (
                    <button key={f.id} className="btn btn-sm" style={{ background: ft.bg, color: ft.color, border: 'none', fontSize: 11, fontWeight: 600 }}
                      onClick={() => downloadFile(f)}>
                      📄 {ft.label}: {f.originalName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
